import { z } from 'zod';
import {
  createModel,
  currencySchema,
  partySchema,
  QueryMany,
  QueryOne,
} from '../../common';
import { dateSchema, objectIdSchema, priceSchema } from '../../validation';
import { UTagSharedSchema } from '../utag';
import { accountStatusSchema } from './account.status.model';
import { accountSettingsSchema } from './account.settings.model';

/**
 * `Account` — bilateral AR/AP balance between exactly two parties.
 *
 * A 90% mirror of {@link WalletSchema} (same `status` enum, signed
 * `priceSchema` balance, currency, UTags) with `holder` (one party)
 * swapped for `parties` (two parties, canonically ordered).
 *
 * ## Bidirectional, one doc per unordered pair
 *
 * A given pair `(Party A, Party B)` gets EXACTLY ONE Account doc per
 * currency, regardless of who is currently buyer or seller. If today
 * A ships for B (A is seller, balance moves up) and tomorrow B ships
 * for A (B is seller, balance moves down), BOTH invoices live on the
 * same Account and the balance nets out. Direction is pinned per
 * invoice (`Invoice.buyer` / `Invoice.seller`), not per account.
 *
 * Canonical ordering: at insert time `AccountService.findOrCreateForPair`
 * sorts the two `Party._id` strings ascending so `(A,B)` and `(B,A)`
 * resolve to the same doc. The unique compound index on
 * `(partyIds[0], partyIds[1], currency)` enforces this at the DB
 * layer too.
 *
 * ## Balance convention
 *
 * `balance` is held from `parties[0]`'s perspective:
 * - **Positive** = `parties[1]` net-owes `parties[0]` (parties[0]
 *   sold more than they bought).
 * - **Negative** = `parties[0]` net-owes `parties[1]`.
 *
 * The invariant (the direct bidirectional analogue of the existing
 * wallet invariant):
 *
 * ```
 * account.balance =
 *   + sum(invoice.amount.remaining where invoice.seller._id === parties[0]._id and status != 'cancelled')
 *   - sum(invoice.amount.remaining where invoice.seller._id === parties[1]._id and status != 'cancelled')
 * ```
 *
 * Advance payments (no invoice yet) sit as `unusedAmount` on an
 * advance-payment invoice and pull the balance back toward zero as
 * future invoices land.
 *
 * ## Relationship to legacy collections
 *
 * `Wallet` and `Transaction` are NOT touched by any code path that
 * reads or writes `Account` / `AccountTransaction`. They stay frozen
 * as legacy, parked for the future "actual wallet cards" feature.
 */
export const AccountSchema = createModel({
  /**
   * Status of the account. If active, transactions can be posted.
   * Mirrors {@link walletStatusSchema} exactly.
   */
  status: accountStatusSchema,

  /**
   * Signed running balance from `parties[0]`'s POV. Can be negative
   * (the party at index 0 owes the party at index 1). Uses the same
   * {@link priceSchema} as {@link WalletSchema.balance} so all existing
   * `priceSchema` math (rounding, `Price` component, Excel export)
   * works without changes.
   */
  balance: priceSchema,

  /** the currency this account's balance is denominated in */
  currency: currencySchema,

  /**
   * The two parties as full {@link partySchema} snapshots. Canonically
   * ordered (ascending by hex `_id` string) so that an unordered pair
   * maps to exactly one Account doc regardless of who is currently
   * seller/buyer.
   *
   * Plain {@link partySchema} on each side — NOT `invoiceBeneficiarySchema`
   * (which carries `walletId` and is parked with the legacy Wallet
   * collection).
   */
  parties: z.tuple([partySchema, partySchema]),

  /**
   * Flat ordered ids mirroring `parties[]` for cheap `$in` /
   * exact-match lookups without unwinding the embedded party docs.
   * Always `[parties[0]._id, parties[1]._id]` in the same canonical
   * order.
   */
  partyIds: z.tuple([objectIdSchema, objectIdSchema]),

  /**
   * Tracks the last time the balance moved in either direction.
   * Simpler than wallet's `lastInDate` / `lastOutDate` because
   * direction here is per-invoice (not per-account), so "in" vs "out"
   * at the account level is ambiguous.
   */
  lastActivityAt: dateSchema.optional(),

  /**
   * Optional per-pair settings overriding `Client.settings` /
   * `Org.settings`. See {@link accountSettingsSchema}.
   */
  settings: accountSettingsSchema,

  UTags: z.array(UTagSharedSchema),
});

/** `Account db model` */
export type Account = z.infer<typeof AccountSchema.model>;
export type AccountData = z.infer<typeof AccountSchema.data>;
export type AccountPartial = z.infer<typeof AccountSchema.partial>;
export type AccountQueryOne = QueryOne<Account>;
export type AccountQueryMany = QueryMany<Account>;
